import { useCallback, useState } from 'react';

import {
	type DefaultMessagesBody,
	type DefaultMessagesResponse,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

// Contract between Jira and the placement
export type AppProps = {
	/** Called on each dismissal; the next eligible message, if any, takes its place. */
	onDismiss?: () => void;
	/**
	 * Called once per eligible message, never when there is nothing to show, so an experiment
	 * check passed in here only exposes users with an eligible message. The Choreographer can
	 * still hold the message back afterwards if another message is on screen.
	 */
	shouldShowMessage?: () => boolean;
};

// Contract between the placement and its messages
export type PlacementRenderProps = DefaultMessagesBody & {
	onDismiss: () => void;
};

export type PlacementMessages = {
	render: FrontendStage<PlacementRenderProps>;
};

type Props = AppProps & { Messages: PlacementMessages };

export const JiraIssueViewCommentTopBanner = ({
	Messages,
	onDismiss,
	shouldShowMessage,
}: Props): JSX.Element | null => {
	// A failed fetch renders nothing rather than throwing, so the comment form never breaks.
	const { data } = usePlacementApi<DefaultMessagesResponse>('/');
	const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());
	const [decision, setDecision] = useState<{ messageInstanceId: string; shouldShow: boolean }>();

	const message = data?.messages.find(
		({ messageInstanceId }) => !dismissedIds.has(messageInstanceId),
	);
	const messageInstanceId = message?.messageInstanceId;

	const handleDismiss = useCallback(() => {
		if (messageInstanceId) {
			setDismissedIds((prev) => new Set(prev).add(messageInstanceId));
		}
		onDismiss?.();
	}, [messageInstanceId, onDismiss]);

	if (!message) {
		return null;
	}

	let shouldShow =
		decision?.messageInstanceId === message.messageInstanceId ? decision.shouldShow : undefined;
	if (shouldShow === undefined) {
		shouldShow = shouldShowMessage?.() ?? true;
		// Remembered per message, so later renders with a new callback do not re-check. A render that
		// React discards (StrictMode, an interrupted transition) can repeat it; Statsig dedupes exposure.
		setDecision({ messageInstanceId: message.messageInstanceId, shouldShow });
	}

	if (!shouldShow) {
		return null;
	}

	return <Messages.render key={message.messageInstanceId} {...message} onDismiss={handleDismiss} />;
};
